import React from "react";
import styled from "styled-components";
import { Timeline_Detail } from "./Timeline_Detail";
import {
    read_timeline_date_label,
    read_timeline_note_label,
    read_timeline_value_label
} from "../../utils/timeline/timeline_formatting";

const TimelineSass = styled.div`
    display: grid;
    gap: 0.6rem;
`;

const TimelineProviderDetail = styled.div`
    display: grid;
    grid-template-columns: minmax(8rem, 12rem) minmax(0, 1fr);
    gap: 1rem;
    align-items: start;

    h6 {
        color: ${p => p.theme.color};
    }
`;

const TimelineProviders = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

const TimelineProvider = styled.span`
    padding: 0.35rem 0.6rem;
    border: 0.1rem solid color-mix(in srgb, ${p => p.$color} 36%, transparent);
    border-radius: var(--smallRadius);
    color: ${p => p.$color};
    background: color-mix(in srgb, ${p => p.$color} 12%, transparent);
    font-weight: 700;
`;

const providerRows = {
    meta: { label: 'Meta Ads', color: 'var(--facebook)' },
    google: { label: 'Google Ads', color: 'var(--google)' }
};

const read_provider = (value) => {
    const provider = `${value?.provider || value || ''}`.trim().toLowerCase().replace(/[\s-]+/g, '_');

    return ['google', 'google_ads', 'googleads'].includes(provider)
        ? 'google'
        : ['meta', 'meta_ads', 'facebook', 'facebook_ads'].includes(provider) ? 'meta' : '';
};

// Reads the sold platforms first, then exact legacy platform evidence.
export const read_sass_ad_providers = (event = {}) => {
    const explicitProviders = [
        event?.detail?.adProviders,
        event?.to?.adProviders,
        event?.data?.adProviders,
        event?.adProviders
    ].find(Array.isArray) || [];
    const inferredProviders = [
        event?.to?.activeQuest,
        event?.data?.activeQuest,
        event?.activeQuest
    ].map(read_provider).filter(Boolean);
    const providerEvidence = [
        ...explicitProviders.map(read_provider),
        ...inferredProviders,
        event?.to?.googleCustomerId || event?.data?.googleCustomerId ? 'google' : '',
        event?.to?.facebookPageId || event?.data?.facebookPageId ? 'meta' : ''
    ].filter(Boolean);

    return [...new Set(providerEvidence)].filter((provider) => providerRows[provider]);
};

// Displays schedule details for SASS lifecycle events after the initial handoff.
const Timeline_Sass_Scheduled = (props) => {
    return (
        <TimelineSass>
            <Timeline_Detail
                label={props.t("Schedule")}
                value={read_timeline_date_label(props.event?.data?.rescheduledAt || props.event?.data?.schedule || props.event?.to?.meetingDate) || read_timeline_value_label(props.event?.data?.rescheduledAt || props.event?.data?.schedule, "")}
            />
            <Timeline_Detail label={props.t("Note")} value={read_timeline_note_label(props.event)} />
        </TimelineSass>
    );
};

// Preserves a genuine handoff note without repeating generated SASS details.
const Timeline_Sass_Handoff = (props) => {
    return (
        <Timeline_Detail label={props.t("Note")} value={read_timeline_note_label(props.event)} />
    );
};

// Shows every ad platform sold with the paid SASS card.
const Timeline_Sass_Paid = (props) => {
    const providers = read_sass_ad_providers(props.event);

    return (
        <TimelineSass>
            {providers.length ? (
                <TimelineProviderDetail data-testid="sass-paid-providers">
                    <h6>{props.t('Quest ads')}</h6>
                    <TimelineProviders>
                        {providers.map((provider) => (
                            <TimelineProvider
                                key={provider}
                                $color={providerRows[provider].color}
                                data-provider={provider}
                                title={`${props.t('Quest ads')}: ${props.t(providerRows[provider].label)}`}
                                aria-label={`${props.t('Quest ads')}: ${props.t(providerRows[provider].label)}`}
                            >
                                {props.t(providerRows[provider].label)}
                            </TimelineProvider>
                        ))}
                    </TimelineProviders>
                </TimelineProviderDetail>
            ) : null}
            <Timeline_Detail label={props.t('Note')} value={read_timeline_note_label(props.event)} />
        </TimelineSass>
    );
};

const timelineSassRendererLookup = {
    SASS: Timeline_Sass_Handoff,
    SASS_PAID: Timeline_Sass_Paid
};

// Selects the compact initial handoff or scheduled lifecycle renderer.
export const Timeline_Sass = (props) => {
    const TimelineSassRenderer = timelineSassRendererLookup[props.event?.type] || Timeline_Sass_Scheduled;

    return (
        <TimelineSassRenderer event={props.event} t={props.t} />
    );
};
